import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardHeader from "../../components/app_components/DashboardHeader";
import MobileBottomNav from "../../components/app_components/mobile/MobileBottomNav";
import { 
  Home, Bell, BellDot, ShoppingBag, Settings, Users, ShoppingCart, Share2 
} from 'lucide-react';

import {
  MobileSharingSpacesHeader,
  MobileSharingSpacesCard,
  MobileCreateSpaceForm,
  MobileDeleteConfirmationModal
} from "../../components/app_components/share_subs/mobile_share_subs";

import { getCurrencySymbol } from '../../utils/Currency';
import { createNotification } from '../../helper/NotificationService';
import { useTranslation } from '../../contexts/LanguageContext';
import { useAuth } from '../../App'; 

const MobileShareSubs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user, httpClient, validateToken } = useAuth(); // Get auth context
  
  const [unreadCount, setUnreadCount] = useState(0);
  const [sharingSpaces, setSharingSpaces] = useState([]);
  const [isPremium, setIsPremium] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [spaceName, setSpaceName] = useState('');
  const [spaceDescription, setSpaceDescription] = useState('');
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState([]);
  const [inviteEmails, setInviteEmails] = useState(['']);
  const [friends, setFriends] = useState([]);
  const [selectedFriends, setSelectedFriends] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmationModal, setConfirmationModal] = useState({ open: false, space: null });
  const [activeTab, setActiveTab] = useState('spaces');
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [activeSpace, setActiveSpace] = useState(null);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [showAddSubscriptionModal, setShowAddSubscriptionModal] = useState(false);

  useEffect(() => {
    if (sharingSpaces.length > 0 && !activeSpace) {
      setActiveSpace(sharingSpaces[0]);
    }
  }, [sharingSpaces, activeSpace]);

  const fetchUnreadNotifications = async () => {
    try {
      if (!httpClient) return;

      const response = await httpClient.request('/api/notifications');
      
      if (!response.ok) {
        throw new Error(t('sharesubs.errors.failedToFetchNotifications'));
      }

      const notifications = await response.json();
      const unread = notifications.filter(notif => !notif.read_status).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error fetching notifications:', err);
    }
  };

  useEffect(() => {
    if (!user) {
      navigate("/demo");
      return;
    }
    
    fetchUnreadNotifications();
    const interval = setInterval(() => {
      fetchUnreadNotifications();
    }, 30000);
    return () => clearInterval(interval);
  }, [user, httpClient]);

  const navItems = [
    { icon: Home, label: t('sharesubs.nav.home'), path: "/mobile-dashboard" },
    { 
      icon: unreadCount > 0 ? BellDot : Bell,
      label: t('sharesubs.nav.notifications'),
      path: "/mobile-notifications",
      badge: unreadCount > 0 ? unreadCount : null
    },
    { icon: ShoppingCart, label: t('sharesubs.nav.marketplace'), path: "/mobile-marketplace" },
    { icon: Share2, label: t('sharesubs.nav.shareSubs'), path: "/mobile-share-subs" },
    { icon: ShoppingBag, label: t('sharesubs.nav.connectBank'), path: "/mobile-connectBank" },
    { icon: Users, label: t('sharesubs.nav.partners'), path: "/mobile-partners" },
    { icon: Settings, label: t('sharesubs.nav.settings'), path: "/mobile-account-settings" },
  ];

  // Fetch premium status
  const fetchPremiumStatus = async () => {
    try {
      if (!user) {
        setIsPremium(false);
        return;
      }

      // Try dedicated premium status endpoint
      try {
        const premiumRes = await httpClient.request('/api/user/premium-status');
        if (premiumRes.ok) {
          const premiumData = await premiumRes.json();
          setIsPremium(premiumData.isPremium);
          return;
        }
      } catch (premiumError) {
        console.error('Error fetching premium status:', premiumError);
      }

      // Fallback to user account endpoint
      const fallbackRes = await httpClient.request('/api/user/account');
      if (fallbackRes.ok) {
        const fallbackData = await fallbackRes.json();
        setIsPremium(fallbackData.userPlan === 'premium');
      }
    } catch (error) {
      console.error('Error fetching user premium status:', error);
      setIsPremium(false);
    }
  };

  const fetchUserData = async () => {
    try {
      setIsLoading(true);
      if (!httpClient || !user) return;

      // Fetch premium status
      await fetchPremiumStatus();
      
      // Fetch sharing spaces
      const spacesResponse = await httpClient.request('/api/sharing-spaces');
      if (spacesResponse.ok) {
        const spacesData = await spacesResponse.json();
        const transformedSpaces = spacesData.map(space => ({
          ...space,
          _id: space._id, // Preserve original _id
          id: space._id?.$oid || space._id?.toString() || space.id,
          userSubscriptions
        }));
        setSharingSpaces(transformedSpaces);
      }
      
      // Fetch user subscriptions
      const subsResponse = await httpClient.request('/api/subscription-list');
      if (subsResponse.ok) {
        const subsData = await subsResponse.json();
        if (Array.isArray(subsData)) {
          setUserSubscriptions(subsData);
        } else {
          setUserSubscriptions([]);
          console.error('Unexpected response from subscription-list API:', subsData);
        }
      } else if (subsResponse.status === 401) {
        const revalidated = await validateToken();
        if (!revalidated) {
          navigate("/demo");
          return;
        }
        // Retry the request
        return fetchUserData();
      }
      
      // Fetch friends
      const friendsResponse = await httpClient.request('/api/friends');
      if (friendsResponse.ok) {
        const friendsData = await friendsResponse.json();
        setFriends(friendsData);
      }
      
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching user data:', err);
      setIsLoading(false);
    }
  };
  
  const fetchPendingInvitations = async () => {
    try {
      if (!httpClient) return;

      const response = await httpClient.request('/api/invitations/pending');
      
      if (response.ok) {
        const data = await response.json();
        setPendingInvitations(data);
      }
    } catch (error) {
      console.error("Failed to fetch invitations:", error);
    }
  };
  
  const handleDeleteSpace = async () => {
    if (!confirmationModal.space || !httpClient) return;
  
    setIsLoading(true);
    const space = confirmationModal.space;
  
    try {
      const spaceId = space._id?.$oid || space._id || space.id;

      console.log("Deleting sharing space...", space);

      const response = await httpClient.request(
        `/api/sharing-space/${spaceId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      const result = await response.json();
      console.log("Delete response:", result);

      if (!response.ok) {
        throw new Error(result.error || t('sharesubs.errors.failedToDeleteSpace'));
      }

      await fetchUserData();
      setConfirmationModal({ open: false, space: null });
      setActiveSpace(null);

      if (result.transactions && result.transactions.issue) {
        alert(t('sharesubs.alerts.spaceDeletedWithIssues', { 
          name: space.name, 
          count: result.transactions.deleted 
        }));
      } else {
        alert(t('sharesubs.alerts.spaceDeleted', { name: space.name }));
      }
    } catch (error) {
      console.error('Error deleting sharing space:', error);
      alert(t('sharesubs.errors.failedToDeleteSpace', { error: error.message }));
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRemoveMember = async (space, memberId) => {
    if (memberId === space.admin_id) {
      alert(t('sharesubs.alerts.adminCannotRemove'));
      return;
    }
    
    const isSelfRemoval = parseInt(memberId) === parseInt(user?.userId);
    const action = isSelfRemoval ? "leave" : "remove";
    
    if (isSelfRemoval && !window.confirm(t('sharesubs.modals.leaveSpace.description'))) return;
    
    setIsLoading(true);
    
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request(
        `/api/sharing-space/${space.id}/member/${memberId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t(`sharesubs.errors.failedTo${action === 'leave' ? 'LeaveSpace' : 'RemoveMember'}`));
      }
      
      setSharingSpaces(prev => prev.map(s => {
        if (s.id === space.id) {
          return {
            ...s,
            members: s.members.filter(m => m.member_id !== memberId)
          };
        }
        return s;
      }));
      
      if (activeSpace && activeSpace.id === space.id) {
        const updatedMembers = activeSpace.members.filter(m => m.member_id !== memberId);
        setActiveSpace({
          ...activeSpace,
          members: updatedMembers
        });
        
        if (isSelfRemoval) {
          setActiveSpace(null);
        }
      }
      
      alert(isSelfRemoval ? t('sharesubs.alerts.leftSpace') : t('sharesubs.alerts.memberRemoved'));
    } catch (error) {
      console.error(`Error ${action}ing member:`, error);
      alert(t(`sharesubs.errors.failedTo${action === 'leave' ? 'LeaveSpace' : 'RemoveMember'}`, { error: error.message }));
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRemoveSpaceSubscription = async (space, merchantId) => {
    setIsLoading(true);
    
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request(
        `/api/sharing-space/${space.id}/plan/${merchantId}`,
        {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('sharesubs.errors.failedToRemoveSubscription'));
      }

      // Update local state
      setSharingSpaces(prev => prev.map(s => {
        if (s.id === space.id) {
          return {
            ...s,
            plan_list: s.plan_list.filter(plan => plan.merchant_id !== merchantId)
          };
        }
        return s;
      }));

      if (activeSpace && activeSpace.id === space.id) {
        setActiveSpace(prev => ({
          ...prev,
          plan_list: prev.plan_list.filter(plan => plan.merchant_id !== merchantId)
        }));
      }

      alert(t('sharesubs.alerts.subscriptionRemoved'));
    } catch (error) {
      console.error('Error removing subscription:', error);
      alert(t('sharesubs.errors.failedToRemoveSubscription', { error: error.message }));
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRemovePersonalSubscription = async (merchant_id) => {
    setIsLoading(true);
    
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request('/api/delete-subscription', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ merchant_id })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || t('sharesubs.errors.failedToRemoveSubscription'));
      }
      
      // Update local state
      setUserSubscriptions(prev => 
        prev.filter(sub => sub.merchant_id !== merchant_id)
      );
      
      alert(t('sharesubs.alerts.personalSubscriptionRemoved'));
      return true;
    } catch (error) {
      console.error('Error removing personal subscription:', error);
      alert(t('sharesubs.errors.failedToRemoveSubscription', { error: error.message }));
      return false;
    } finally {
      setIsLoading(false);
    }
  };
  
  const updateMemberBalance = async (spaceId, memberId, newBalance) => {
    setIsLoading(true);
    
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request(
        `/api/sharing-space/${spaceId}/member/${memberId}/balance`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ balance: newBalance })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('sharesubs.errors.failedToUpdateBalance'));
      }
      
      setSharingSpaces(prev => prev.map(space => {
        if (space.id === spaceId) {
          return {
            ...space,
            members: space.members.map(m => 
              m.member_id === memberId ? { ...m, balance: newBalance } : m
            )
          };
        }
        return space;
      }));
      
      if (activeSpace && activeSpace.id === spaceId) {
        setActiveSpace(prev => ({
          ...prev,
          members: prev.members.map(m => 
            m.member_id === memberId ? { ...m, balance: newBalance } : m
          )
        }));
      }
      
      alert(t('sharesubs.alerts.balanceUpdated'));
    } catch (error) {
      console.error('Error updating balance:', error);
      alert(t('sharesubs.errors.failedToUpdateBalance', { error: error.message }));
    } finally {
      setIsLoading(false);
    }
  };
  
  const inviteMembersToSpace = async (space, newMembers) => {
    setIsLoading(true);
    
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request(
        `/api/sharing-space/${space.id}/invite`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ members: newMembers })
        }
      );
      
      const responseData = await response.json();
      
      if (!response.ok) {
        if (responseData.duplicates) {
          throw new Error(t('sharesubs.errors.duplicateEmails', { emails: responseData.duplicates.join(', ') }));
        }
        throw new Error(responseData.error || t('sharesubs.errors.failedToInviteMembers'));
      }
      
      // Update local state with new members
      const newSpaceMembers = newMembers.map(member => ({
        ...member,
        member_email: member.email,
        member_firstname: member.firstName || '',
        member_last_name: member.lastName || '',
        request_status: 'pending',
        joining_date: null,
        member_id: member.userId || null,
        balance: 0,
      }));
      
      const updatedSpaces = sharingSpaces.map(s => {
        if (s.id === space.id) {
          return {
            ...s,
            members: [...s.members, ...newSpaceMembers]
          };
        }
        return s;
      });
      
      setSharingSpaces(updatedSpaces);
      
      if (activeSpace && activeSpace.id === space.id) {
        setActiveSpace(updatedSpaces.find(s => s.id === space.id));
      }
      
      setShowAddMemberModal(false);
      alert(t('sharesubs.alerts.invitationsSent'));
    } catch (error) {
      console.error('Error inviting members:', error);
      alert(t('sharesubs.errors.failedToInviteMembers', { error: error.message }));
    } finally {
      setIsLoading(false);
    }
  };
  
  const addSubscriptionsToSpace = async (space, subscriptionIds) => {
    setIsLoading(true);
    
    // Get full subscription objects
    const subscriptionsToAdd = userSubscriptions.filter(
      sub => subscriptionIds.includes(sub.id)
    );
    
    // Format subscriptions with proper types
    const formattedSubscriptions = subscriptionsToAdd.map(sub => {
      // Convert plan_id to integer for standard subscriptions
      let planId = sub.plan_id;
      if (!sub.is_custom && planId) {
        planId = parseInt(planId, 10);
      }
      
      return {
        id: sub.id,
        merchant_id: sub.merchant_id,
        plan_id: planId,
        name: sub.name,
        price: sub.price,
        currency: sub.currency,
        billing_interval: sub.billing_interval,
        start_date: sub.start_date,
        logo: sub.logo,
        is_custom: sub.is_custom
      };
    });

    try {
      if (!httpClient) return;
      
      const spaceId = space._id || space.id;
      
      const response = await httpClient.request(
        `/api/sharing-space/${spaceId}/subscriptions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ subscriptions: formattedSubscriptions })
        }
      );
      
      // Handle non-JSON responses
      let responseData;
      try {
        responseData = await response.json();
      } catch (e) {
        const text = await response.text();
        throw new Error(t('sharesubs.errors.invalidResponse'));
      }
      
      if (!response.ok) {
        if (responseData.duplicates) {
          throw new Error(t('sharesubs.errors.duplicateSubscriptions', { subscriptions: responseData.duplicates.join(', ') }));
        }
        throw new Error(responseData.error || t('sharesubs.errors.failedToAddSubscriptions'));
      }
      
      // Update local state with the new plans
      const newSubscriptions = responseData.newPlans.map(plan => ({
        ...plan,
        // Convert ObjectId to string if needed
        merchant_id: plan.merchant_id ? plan.merchant_id.toString() : null
      }));
      
      const updatedSpaces = sharingSpaces.map(s => {
        if (s.id === space.id) {
          return {
            ...s,
            plan_list: [...s.plan_list, ...newSubscriptions]
          };
        }
        return s;
      });
      
      setSharingSpaces(updatedSpaces);
      
      if (activeSpace && activeSpace.id === space.id) {
        setActiveSpace(updatedSpaces.find(s => s.id === space.id));
      }
      
      setShowAddSubscriptionModal(false);
      alert(t('sharesubs.alerts.subscriptionsAdded', { count: newSubscriptions.length }));
    } catch (error) {
      console.error('Error adding subscriptions:', error);
      
      // More detailed error message
      let errorMessage = error.message;
      if (error.message.includes('duplicate')) {
        errorMessage = error.message;
      } else {
        errorMessage = t('sharesubs.errors.failedToAddSubscriptions', { error: error.message });
      }
      
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleCancelInvitation = async (space, email) => {
    if (!window.confirm(t('sharesubs.modals.cancelInvitation.description', { email }))) return;
    
    setIsLoading(true);
    
    try {
      if (!httpClient) return;
      
      const response = await httpClient.request(
        `/api/sharing-space/${space.id}/cancel-invite`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ email })
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('sharesubs.errors.failedToCancelInvitation'));
      }
      
      const updatedSpaces = sharingSpaces.map(s => {
        if (s.id === space.id) {
          return {
            ...s,
            members: s.members.filter(m => m.member_email !== email)
          };
        }
        return s;
      });
      
      setSharingSpaces(updatedSpaces);
      
      if (activeSpace && activeSpace.id === space.id) {
        setActiveSpace(updatedSpaces.find(s => s.id === space.id));
      }
      
      alert(t('sharesubs.alerts.invitationCanceled'));
    } catch (error) {
      console.error('Error canceling invitation:', error);
      alert(t('sharesubs.errors.failedToCancelInvitation', { error: error.message }));
    } finally {
      setIsLoading(false);
    }
  };
  
  useEffect(() => {
    if (user && httpClient) {
      fetchUserData();
      fetchUnreadNotifications();
      fetchPendingInvitations();
    } else if (!user) {
      navigate("/demo");
    }
  }, [user, httpClient]);

  const resetFormState = () => {
    setShowCreateForm(false);
    setSpaceName('');
    setSpaceDescription('');
    setSelectedSubscriptions([]);
    setInviteEmails(['']);
    setSelectedFriends([]);
  };
  
  const handleSpaceCreated = async () => {
    await fetchUserData();
    resetFormState();
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <DashboardHeader isAuthenticated={!!user} user={user} />
      
      <main className="flex-1 overflow-auto pb-20">
        <div className="p-4 space-y-4">
          {/* Header with conditional premium gradient */}
          <div className={`${isPremium 
            ? 'bg-gradient-to-r from-[#253985] to-amber-500' 
            : 'bg-[#253985]'} text-white p-4 rounded-lg transition-all duration-500`}>
            <h1 className="text-xl font-bold">{t('sharesubs.title')}</h1>
            <p className="text-white/90 text-sm">{t('sharesubs.description')}</p>
          </div>

          <MobileSharingSpacesCard
            isPremium={isPremium}
            sharingSpaces={sharingSpaces}
            setSharingSpaces={setSharingSpaces} 
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            isLoading={isLoading}
            activeSpace={activeSpace}
            setActiveSpace={setActiveSpace}
            onCreateSpace={() => {
              if (!isPremium && sharingSpaces.length >= 3) return;
              setShowCreateForm(true);
            }}
            onUpgrade={() => navigate('/mobile-upgrade')}
            showCreateForm={showCreateForm}
            pendingInvitations={pendingInvitations}
            setPendingInvitations={setPendingInvitations}
            fetchUserData={fetchUserData}
            fetchPendingInvitations={fetchPendingInvitations}
            hasPendingInvites={pendingInvitations.length > 0}
            onDeleteSpace={(space) => setConfirmationModal({ open: true, space })}
            onRemoveMember={handleRemoveMember}
            onRemoveSpaceSubscription={handleRemoveSpaceSubscription}
            onRemovePersonalSubscription={handleRemovePersonalSubscription}
            onUpdateBalance={updateMemberBalance}
            onInviteNewMembers={inviteMembersToSpace}
            onAddSubscriptions={addSubscriptionsToSpace}
            onCancelInvitation={handleCancelInvitation}
            friends={friends}
            userSubscriptions={userSubscriptions}
            formProps={{
              spaceName,
              setSpaceName,
              spaceDescription,
              setSpaceDescription,
              userSubscriptions,
              selectedSubscriptions,
              handleSubscriptionToggle: (subscriptionId) => {
                setSelectedSubscriptions(prev => 
                  prev.includes(subscriptionId)
                    ? prev.filter(id => id !== subscriptionId)
                    : [...prev, subscriptionId]
                );
              },
              friends,
              selectedFriends,
              handleFriendToggle: (friend) => {
                setSelectedFriends(prev => 
                  prev.some(f => f.userId === friend.userId)
                    ? prev.filter(f => f.userId !== friend.userId)
                    : [...prev, friend]
                );
              },
              inviteEmails,
              handleEmailChange: (index, value) => {
                const newEmails = [...inviteEmails];
                newEmails[index] = value;
                setInviteEmails(newEmails);
              },
              addEmailField: () => setInviteEmails([...inviteEmails, '']),
              removeEmailField: (index) => {
                if (inviteEmails.length > 1) {
                  const newEmails = [...inviteEmails];
                  newEmails.splice(index, 1);
                  setInviteEmails(newEmails);
                }
              },
              handleSubmit: async (e) => {
                e.preventDefault();
                if (!spaceName.trim() || !httpClient || !user) return;
                
                setIsLoading(true);
                
                // Prepare subscriptions with logo and plan_id as integer
                const selectedSubs = userSubscriptions
                  .filter(sub => selectedSubscriptions.includes(sub.id))
                  .map(sub => {
                    let planId = sub.plan_id;
                    if (!sub.is_custom && planId) {
                      planId = parseInt(planId, 10);
                    }
                    
                    return {
                      id: sub.id,
                      merchant_id: sub.merchant_id,
                      plan_id: planId,
                      name: sub.name,
                      price: sub.price,
                      currency: sub.currency,
                      billing_interval: sub.billing_interval,
                      start_date: sub.start_date,
                      logo: sub.logo,
                      is_custom: sub.is_custom
                    };
                  });
                
                // Prepare members
                const friendMembers = selectedFriends.map(friend => ({
                  email: friend.email,
                  userId: friend.userId
                }));
                
                const emailMembers = inviteEmails
                  .filter(email => email.trim() !== '' && email.includes('@'))
                  .map(email => {
                    const friend = friends.find(f => f.email === email);
                    return {
                      email,
                      userId: friend?.userId || null
                    };
                  });
                
                const allMembers = [...friendMembers, ...emailMembers];
                
                try {
                  const response = await httpClient.request('/api/sharing-space', {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                      name: spaceName,
                      description: spaceDescription,
                      subscriptions: selectedSubs,
                      members: allMembers
                    })
                  });
                  
                  const responseData = await response.json();
                  
                  if (!response.ok) {
                    console.error('Backend error response:', responseData);
                    throw new Error(responseData.error || t('sharesubs.errors.failedToCreateSpace'));
                  }
                  
                  handleSpaceCreated();
                  alert(t('sharesubs.alerts.spaceCreated', { name: spaceName }));
                } catch (error) {
                  console.error('Error creating sharing space:', error);
                  alert(t('sharesubs.errors.failedToCreateSpace', { error: error.message }));
                } finally {
                  setIsLoading(false);
                }
              },
              isLoading,
              onCancel: resetFormState
            }}
          />
        </div>
      </main>

      {confirmationModal.open && (
        <MobileDeleteConfirmationModal 
          space={confirmationModal.space}
          onClose={() => setConfirmationModal({ open: false, space: null })}
          onDelete={handleDeleteSpace}
          isLoading={isLoading}
        />
      )}

      <MobileBottomNav 
        navItems={navItems} 
        currentPath={location.pathname}
        maxVisibleItems={5}
        navigate={navigate}
        className="fixed bottom-0 w-full"
      />
    </div>
  );
};

export default MobileShareSubs;